import { useState, useEffect, useContext, useRef } from "react";
import PropTypes from "prop-types";
import { useLocation, NavLink, matchPath } from "react-router-dom";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { useFromStore } from "hooks";
import lh from "helpers/linkHandler";
import UserLinks from "./mobile-components/UserLinks";
import MobileSearch from "./mobile-components/Search";
import MobileBreadcrumb from "./mobile-components/Breadcrumb";
import { FocusTrap } from "focus-trap-react";
import IconComposer from "global/components/utility/IconComposer";
import { FrontendModeContext } from "helpers/contexts";
import Authorize from "hoc/Authorize";
import BodyClass from "hoc/BodyClass";

export default function NavigationMobile({
  links,
  commonActions,
  backendButton,
  mode,
  journalIsActive
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const context = useContext(FrontendModeContext);
  const authentication = useFromStore("authentication");

  const [expanded, setExpanded] = useState([]);
  const [open, setOpen] = useState(false);

  const hasLinks = links && links.length > 0;
  const isStandalone = context.isStandalone;

  const pathForLink = link => {
    const args = link.args || [];
    return lh.link(link.route, ...args);
  };

  const getActiveRoutes = () => {
    if (!links) return [];
    const active = [];
    links.forEach(link => {
      const route = lh.routeFromName(link.route);
      if (route && matchPath(location.pathname, route) !== null) {
        if (link.route === "frontendProjects" && journalIsActive) {
          active.push("frontendJournals");
        } else if (link.route === "backendProjects" && journalIsActive) {
          active.push("backendJournals");
        } else {
          active.push(link.route);
        }
      }
    });
    return active;
  };

  const prevLocationRef = useRef(location);

  useEffect(() => {
    if (!open) {
      prevLocationRef.current = location;
      return;
    }
    if (prevLocationRef.current.pathname !== location.pathname) {
      setExpanded([]);
      setOpen(false);
    }
    prevLocationRef.current = location;
  }, [location, open]);

  const toggleExpanded = key => {
    setExpanded(prevExpanded => {
      if (prevExpanded.includes(key)) {
        return prevExpanded.filter(k => k !== key);
      } else {
        return [...prevExpanded, key];
      }
    });
  };

  const createExpandToggleHandler = key => {
    return event => {
      event.preventDefault();
      toggleExpanded(key);
    };
  };

  const closeNavigation = () => {
    setOpen(false);
    setExpanded([]);
  };

  const toggleOpen = () => {
    if (open) {
      closeNavigation();
    } else {
      setOpen(true);
      setExpanded(getActiveRoutes());
    }
  };

  const triggerIcon = open ? "close32" : "menu32";

  const renderStandaloneHeading = () => {
    if (!isStandalone || !context.project) return null;

    const { titleFormatted, subtitleFormatted } = context.project;

    return (
      <li className="nested-nav__item">
        <div className="nested-nav__standalone-heading">
          {titleFormatted && (
            <div
              className="nested-nav__standalone-title"
              dangerouslySetInnerHTML={{ __html: titleFormatted }}
            />
          )}
          {subtitleFormatted && (
            <div
              className="nested-nav__standalone-subtitle"
              dangerouslySetInnerHTML={{ __html: subtitleFormatted }}
            />
          )}
        </div>
      </li>
    );
  };

  const renderExternalLink = link => {
    return (
      <a
        href={link.externalUrl}
        target={link.newTab ? "_blank" : null}
        rel="noopener noreferrer"
        className="nested-nav__link"
      >
        {link.label}
      </a>
    );
  };

  const renderManifoldLink = link => {
    const path = pathForLink(link);
    const exact = path === "/";

    return (
      <NavLink
        to={path}
        exact={exact}
        onClick={closeNavigation}
        target={link.newTab ? "_blank" : null}
        className={({ isActive }) =>
          classnames("nested-nav__link", {
            active: isActive
          })
        }
      >
        {t(link.label)}
      </NavLink>
    );
  };

  const renderItem = (link, index) => {
    if (link.hideInNav) return null;
    const children = link.children || [];
    const hasChildren = children && children.length > 0;
    const isExpanded = expanded.includes(link.route);
    const wrapperClasses = classnames({
      "nested-nav__item": true,
      "nested-nav__grid-item": true,
      "nested-nav__item--nested": hasChildren,
      "nested-nav__item--open": isExpanded
    });

    return (
      <li key={`${link.label}-${index}`} className={wrapperClasses}>
        {link.route ? renderManifoldLink(link) : renderExternalLink(link)}
        {hasChildren && (
          <button
            onClick={createExpandToggleHandler(link.route)}
            className="nested-nav__disclosure-button"
            aria-haspopup="true"
            aria-expanded={isExpanded}
          >
            <span className="screen-reader-text">
              {isExpanded
                ? t("navigation.mobile.close_submenu")
                : t("navigation.mobile.open_submenu")}
            </span>
            <IconComposer
              icon="disclosureDown16"
              size="default"
              className="nested-nav__disclosure-icon"
            />
          </button>
        )}
        {hasChildren && (
          <ul className="nested-nav__list nested-nav__list--nested">
            {children.map(child => renderItem(child))}
          </ul>
        )}
      </li>
    );
  };

  const renderNavigationMenu = () => {
    return (
      <BodyClass className={"no-scroll"}>
        <FocusTrap
          focusTrapOptions={{
            allowOutsideClick: true,
            escapeDeactivates: closeNavigation
          }}
        >
          <div className="nested-nav__content">
            <ul
              aria-label="Page Links"
              className="nested-nav__list nested-nav__list--primary-links"
            >
              {isStandalone && renderStandaloneHeading()}
              {hasLinks &&
                links.map((link, index) => {
                  if (link.ability || link.kind)
                    return (
                      <Authorize
                        key={`${link.route}-wrapped`}
                        entity={link.entity}
                        ability={link.ability}
                        kind={link.kind}
                      >
                        {renderItem(link, index)}
                      </Authorize>
                    );
                  return renderItem(link, index);
                })}
              {mode === "frontend" && (
                <li className="nested-nav__item">
                  <MobileSearch closeNavigation={closeNavigation} />
                </li>
              )}
            </ul>
            <UserLinks
              authentication={authentication}
              commonActions={commonActions}
              backendButton={backendButton}
              mode={mode}
              closeNavigation={closeNavigation}
            />
          </div>
        </FocusTrap>
      </BodyClass>
    );
  };

  const navClasses = classnames({
    "hide-82": mode === "frontend",
    "hide-100": mode === "backend",
    "nested-nav": true,
    "nested-nav--open": open,
    "nested-nav--dark": mode === "backend"
  });

  return (
    <>
      {hasLinks && (
        <MobileBreadcrumb links={links} journalIsActive={journalIsActive} />
      )}
      <nav
        className={navClasses}
        aria-label={t("navigation.mobile.aria_label")}
      >
        {open && renderNavigationMenu()}
      </nav>
      <button
        onClick={toggleOpen}
        className="mobile-nav-toggle"
        aria-haspopup
        aria-expanded={open}
      >
        <span className="screen-reader-text">
          {open
            ? t("navigation.mobile.toggle_closed")
            : t("navigation.mobile.toggle_open")}
        </span>
        <IconComposer
          icon={triggerIcon}
          size="default"
          className="mobile-nav-trigger__icon"
        />
      </button>
    </>
  );
}

NavigationMobile.displayName = "Navigation.Mobile";

NavigationMobile.propTypes = {
  links: PropTypes.array,
  commonActions: PropTypes.object.isRequired,
  backendButton: PropTypes.oneOfType([PropTypes.func, PropTypes.element]),
  mode: PropTypes.oneOf(["backend", "frontend"]).isRequired,
  journalIsActive: PropTypes.bool
};
